import { Form, Formik, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import { message } from 'antd';

import { queryClient } from 'config/query-client';
import {
  createMeasurementUnit,
  updateMeasurementUnit,
} from 'services/collections';
import useTranslation from 'customHooks/translate';
import { MeasurementUnits, MeasurementUnitsCustomError } from 'types';
import { MEASUREMENT_UNITS } from 'events/types';

import { Input } from './styled-components/common-components';
import CustomDialog from './CustomDialog';

const defaultValues = {
  name: '',
};

const MeasurementUnitsForm = ({
  open,
  handleClose,
  measurementUnitUpdate,
  onSuccess,
}) => {
  const [nameError, setNameError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const translation = useTranslation();
  const formRef = useRef<FormikProps<MeasurementUnits>>(null);

  return (
    <CustomDialog
      buttonTitle={
        !!measurementUnitUpdate
          ? `${translation.taxTypes.Update}`
          : `${translation.taxTypes.Create}`
      }
      open={open}
      onSubmit={() => formRef.current?.handleSubmit()}
      handleClose={handleClose}
      title={translation.MeasurementUnits.MeasurementUnit}
      isLoading={loading}
    >
      <Formik
        innerRef={formRef}
        initialValues={measurementUnitUpdate || defaultValues}
        onSubmit={(values, actions) => {
          setLoading(true);
          const method = measurementUnitUpdate
            ? updateMeasurementUnit
            : createMeasurementUnit;
          method({
            ...values,
            id: measurementUnitUpdate ? measurementUnitUpdate.id : undefined,
          })
            .then(() => {
              queryClient.invalidateQueries({
                queryKey: [MEASUREMENT_UNITS],
              });
              handleClose();
              onSuccess(measurementUnitUpdate ? 'updated' : 'added');
            })
            .catch((error: MeasurementUnitsCustomError) => {
              error.response?.data?.Message &&
                message.error(error.response?.data?.Message);
              const errorMessageName = error.response?.data.errors
                ?.Name?.[0] as string | undefined;
              setNameError(errorMessageName || null);
            })
            .finally(() => {
              actions.resetForm({ values: defaultValues });
              setLoading(false);
            });
        }}
        enableReinitialize
      >
        {(props) => (
          <Form>
            <div className="flex flex-col gap-[12px]">
              <label
                className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
                htmlFor="name"
              >
                {translation.MeasurementUnits.MeasurementUnit} *
              </label>
              <Input
                className="d-block"
                type="text"
                name="name"
                placeholder={translation.MeasurementUnits.MeasurementUnit}
                onChange={props.handleChange}
                value={props.values.name || ''}
                style={{
                  borderColor: nameError ? 'red' : '',
                }}
              />
              {nameError && (
                <span className="text-[12px] text-red-600">{nameError}</span>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </CustomDialog>
  );
};

export default MeasurementUnitsForm;
